'use client';

import { useState, useEffect } from 'react';
import { useFrontSession } from '@/components/front-session-provider';
import { MessageSquare, X, Send, Trash2, PlusCircle, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface FeedbackSession {
  id: string;
  title: string;
  messages: { role: 'user' | 'assistant'; content: string }[];
  architectReply?: string;
  unread: boolean;
  createdAt: string;
}

export function FeedbackChat() {
  const session = useFrontSession();
  const [isOpen, setIsOpen] = useState(false);
  const [sessions, setSessions] = useState<FeedbackSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  const isAllowed = session.authenticated && session.roles?.some(r => ['founder', 'team', 'apprentice'].includes(r));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOpen && isAllowed && isVisible) {
      loadSessions();
    }
  }, [isOpen, isAllowed, isVisible]);

  useEffect(() => {
    const saved = localStorage.getItem('show-chat-agent');
    if (saved === 'false') {
      setIsVisible(false);
      setIsOpen(false);
    }

    const handleVisibilityChange = (e: Event) => {
      const customEvent = e as CustomEvent<{ visible: boolean }>;
      setIsVisible(customEvent.detail.visible);
      if (!customEvent.detail.visible) {
        setIsOpen(false);
      }
    };

    window.addEventListener('chatAgentVisibilityChanged', handleVisibilityChange);
    return () => window.removeEventListener('chatAgentVisibilityChanged', handleVisibilityChange);
  }, []);

  const loadSessions = async () => {
    try {
      const res = await fetch('/api/feedback/sessions');
      const data = await res.json();
      if (data.sessions) {
        setSessions(data.sessions);
        if (data.sessions.length > 0 && !activeSessionId) {
          setActiveSessionId(data.sessions[0].id);
          if (data.sessions[0].unread) markAsRead(data.sessions[0].id);
        } else if (data.sessions.length === 0) {
          await createNewSession(data.sessions);
        }
      }
    } catch (e) {
      console.error('Failed to load feedback sessions', e);
    }
  };

  const createNewSession = async (currentSessions: FeedbackSession[]) => {
    if (currentSessions.length >= 5) {
      alert("You've reached the maximum of 5 active feedback sessions. Please clear an old one first.");
      return;
    }
    try {
      const res = await fetch('/api/feedback/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'create' })
      });
      const data = await res.json();
      if (data.session) {
        setSessions(data.sessions);
        setActiveSessionId(data.session.id);
      }
    } catch (e) {
      console.error('Failed to create session', e);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      setSessions(prev => prev.map(s => s.id === id ? { ...s, unread: false } : s));
      await fetch('/api/feedback/sessions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'mark_read', sessionId: id })
      });
    } catch (e) {
      console.error('Failed to mark read', e);
    }
  };

  const deleteSession = async () => {
    if (!activeSessionId) return;
    try {
      const res = await fetch(`/api/feedback/sessions?sessionId=${activeSessionId}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (data.sessions) {
        setSessions(data.sessions);
        setActiveSessionId(data.sessions.length > 0 ? data.sessions[0].id : null);
        if (data.sessions.length === 0) {
          await createNewSession([]);
        }
      }
    } catch (e) {
      console.error('Failed to delete session', e);
    } finally {
      setShowClearConfirm(false);
    }
  };

  const activeSession = sessions.find(s => s.id === activeSessionId);
  const totalUnread = sessions.filter(s => s.unread).length;

  if (!isAllowed || !isVisible) return null;

  const sendMessage = async () => {
    if (!input.trim() || isLoading || !activeSessionId) return;

    const newMsg = { role: 'user' as const, content: input };
    const newMessages = [...(activeSession?.messages || []), newMsg];
    
    // Optimistic update
    setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: newMessages } : s));
    setInput('');
    setIsLoading(true);

    try {
      const res = await fetch('/api/feedback/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          messages: newMessages,
          user: session.user,
          sessionId: activeSessionId
        }),
      });

      const data = await res.json();
      if (data.reply) {
        const cleanReply = data.reply.replace(/```json\n[\s\S]*?\n```/, '').trim();
        if (cleanReply) {
          setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: [...newMessages, { role: 'assistant', content: cleanReply }] } : s));
        }
      }
      // Re-sync to get potential title updates from server
      await loadSessions();
    } catch (e) {
      setSessions(prev => prev.map(s => s.id === activeSessionId ? { ...s, messages: [...newMessages, { role: 'assistant', content: 'Connection error.' }] } : s));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {!isOpen && (
        <Button
          onClick={() => setIsOpen(true)}
          className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-xl z-50 bg-primary hover:bg-primary/90"
          size="icon"
        >
          <div className="relative">
            <MessageSquare className="h-6 w-6 text-primary-foreground" />
            {totalUnread > 0 && (
              <span className="absolute -top-1 -right-1 flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
              </span>
            )}
          </div>
        </Button>
      )}

      {isOpen && (
        <div className="fixed bottom-6 right-6 w-[340px] sm:w-[400px] h-[550px] max-h-[80vh] bg-background border border-border rounded-xl shadow-2xl flex flex-col z-50 overflow-hidden">
          <div className="flex flex-col p-3 border-b bg-muted/40 gap-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm">Feedback</h3>
                {totalUnread > 0 && <Globe className="h-4 w-4 text-primary animate-pulse" />}
              </div>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-500" onClick={() => setShowClearConfirm(true)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => createNewSession(sessions)}>
                  <PlusCircle className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
            
            <Select 
              value={activeSessionId || undefined} 
              onValueChange={(val) => {
                setActiveSessionId(val);
                const s = sessions.find(x => x.id === val);
                if (s?.unread) markAsRead(s.id);
              }}
            >
              <SelectTrigger className="h-8 text-xs w-full bg-background">
                <SelectValue placeholder="Select a session" />
              </SelectTrigger>
              <SelectContent>
                {sessions.map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-xs">
                    {s.unread ? '🔵 ' : ''}{s.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <ScrollArea className="flex-1 p-4">
            {activeSession?.messages.length === 0 && (
              <div className="text-center text-muted-foreground text-sm mt-10">
                Hi {session.user?.username}!
                <br /><br />
                Request features, changes or improvements?<br />
                Report bugs, problems or anything else?
              </div>
            )}
            
            <div className="space-y-4 pb-4">
              {activeSession?.messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg p-3 text-sm whitespace-pre-wrap ${msg.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground'}`}>
                    {msg.content}
                  </div>
                </div>
              ))}
              
              {activeSession?.architectReply && (
                <div className="flex justify-start">
                  <div className="max-w-[85%] rounded-lg p-3 text-sm whitespace-pre-wrap bg-green-500/10 border border-green-500/20 text-foreground">
                    <span className="font-semibold text-green-500 mb-1 block">Dev Team:</span>
                    {activeSession.architectReply}
                  </div>
                </div>
              )}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-muted max-w-[80%] rounded-lg p-3 text-sm animate-pulse">
                    Typing...
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="p-4 border-t bg-background">
            <form 
              onSubmit={(e) => { e.preventDefault(); sendMessage(); }}
              className="flex gap-2"
            >
              <input
                className="flex-1 bg-transparent border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Type your feedback..."
                disabled={!!activeSession?.architectReply}
              />
              <Button type="submit" size="icon" disabled={isLoading || !input.trim() || !!activeSession?.architectReply}>
                <Send className="h-4 w-4" />
              </Button>
            </form>
          </div>
        </div>
      )}

      <Dialog open={showClearConfirm} onOpenChange={setShowClearConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear Session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this feedback session? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClearConfirm(false)}>Cancel</Button>
            <Button className="bg-red-500 hover:bg-red-600 text-white" onClick={deleteSession}>Delete Session</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
